// src/components/ProductionAssistantPanel.jsx
import React, { useMemo, useState, useEffect } from 'react';
import { Snackbar, Alert } from '@mui/material';
import { fetchJson, authHeaders } from '../api';

const GOLD = '#d4a550';
const GOLD_DIM = 'rgba(212,165,80,0.10)';
const GOLD_BORDER = 'rgba(212,165,80,0.28)';
const SURFACE = '#0f0f17';
const BORDER = 'rgba(255,255,255,0.07)';
const TEXT = '#e8e6df';
const TEXT_DIM = 'rgba(232,230,223,0.45)';
const BG_ITEM = '#13131e';

const s = {
  card: {
    position: 'sticky',
    top: 16,
    background: SURFACE,
    border: `1px solid ${BORDER}`,
    borderRadius: 10,
    padding: '14px 16px',
    fontFamily: "'Inter', sans-serif",
    color: TEXT,
  },
  label: {
    fontSize: 10,
    fontFamily: "'Kanit', sans-serif",
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: GOLD,
    marginBottom: 2,
  },
  title: { fontSize: 14, fontWeight: 700, color: TEXT, margin: 0 },
  sub: { fontSize: 11, color: TEXT_DIM, marginTop: 2 },
  divider: { borderTop: `1px solid ${BORDER}`, margin: '12px 0' },
  sectionTitle: {
    fontSize: 11, fontWeight: 600, color: TEXT_DIM,
    textTransform: 'uppercase', letterSpacing: '0.07em',
    marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6,
  },
  row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  chip: (active) => ({
    fontSize: 10, fontWeight: 700, letterSpacing: '0.05em',
    padding: '2px 8px', borderRadius: 20,
    border: `1px solid ${active ? GOLD_BORDER : BORDER}`,
    background: active ? GOLD_DIM : 'transparent',
    color: active ? GOLD : TEXT_DIM,
  }),
  toggle: (active) => ({
    display: 'inline-flex', alignItems: 'center', gap: 6,
    cursor: 'pointer', fontSize: 12, fontWeight: 500,
    color: active ? TEXT : TEXT_DIM, userSelect: 'none',
  }),
  toggleTrack: (active) => ({
    width: 32, height: 18, borderRadius: 9,
    background: active ? GOLD : 'rgba(255,255,255,0.1)',
    border: `1px solid ${active ? GOLD_BORDER : BORDER}`,
    position: 'relative', transition: 'background 0.2s', flexShrink: 0,
  }),
  toggleThumb: (active) => ({
    position: 'absolute', top: 2, left: active ? 14 : 2,
    width: 12, height: 12, borderRadius: '50%',
    background: active ? '#07070a' : 'rgba(255,255,255,0.4)',
    transition: 'left 0.2s',
  }),
  select: {
    background: BG_ITEM, border: `1px solid ${BORDER}`,
    borderRadius: 6, color: TEXT, fontSize: 12,
    padding: '5px 10px', fontFamily: "'Inter', sans-serif",
    cursor: 'pointer', flex: 1, outline: 'none',
  },
  btn: (variant) => ({
    fontSize: 11, fontWeight: 700, padding: '5px 12px', borderRadius: 6,
    cursor: 'pointer', fontFamily: "'Inter', sans-serif",
    border: variant === 'primary' ? `1px solid ${GOLD_BORDER}` : `1px solid ${BORDER}`,
    background: variant === 'primary' ? GOLD_DIM : 'transparent',
    color: variant === 'primary' ? GOLD : TEXT_DIM,
    transition: 'opacity 0.15s', whiteSpace: 'nowrap',
  }),
  comingSoon: {
    fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20,
    background: 'rgba(100,100,255,0.1)', border: '1px solid rgba(100,100,255,0.3)',
    color: '#8888ff', letterSpacing: '0.05em',
  },
};

export default function ProductionAssistantPanel({
  freelancerId, freelancerName,
  initIsFavorite = false, initIsPrioritized = false,
  onTogglePrioritize, initLeadDays = null,
  onToggleFavorite, onUpsertAlert, onPrioritize, token,
}) {
  const [isFavorite, setIsFavorite] = useState(!!initIsFavorite);
  const [lead, setLead] = useState(initLeadDays);
  const [busy, setBusy] = useState(false);
  const [snack, setSnack] = useState(null);
  const [isPrioritized, setIsPrioritized] = useState(!!initIsPrioritized);

  useEffect(() => { setIsFavorite(!!initIsFavorite); }, [initIsFavorite]);

  useEffect(() => {
    if (!freelancerId) return;
    (async () => {
      try {
        const data = await fetchJson(`/alertsjobs/availability/${freelancerId}`, { headers: authHeaders(token) });
        if (data?.active && data?.lead_days) setLead(data.lead_days);
        else setLead(null);
      } catch { }
    })();
  }, [freelancerId, token]);

  const canSaveAlert = useMemo(() => lead === 7 || lead === 15 || lead === 30, [lead]);
  const safeName = (freelancerName && String(freelancerName).trim()) || 'Freelance';

  const doToggleFavorite = async () => {
    const enabled = !isFavorite;
    setIsFavorite(enabled);
    try {
      setBusy(true);
      await onToggleFavorite?.(enabled);
      setSnack({ sev: 'success', msg: enabled ? `${safeName} aggiunto ai preferiti` : `${safeName} rimosso dai preferiti` });
    } catch (err) {
      setIsFavorite(!enabled);
      setSnack({ sev: 'error', msg: err?.message || 'Errore' });
    } finally { setBusy(false); }
  };

  const doSaveAlert = async () => {
    try {
      setBusy(true);
      await onUpsertAlert?.(lead ?? null);
      setSnack({ sev: 'success', msg: lead ? `Alert attivo (${lead} gg)` : 'Alert disattivato' });
    } catch (err) {
      setSnack({ sev: 'error', msg: err?.message || 'Errore' });
    } finally { setBusy(false); }
  };

  return (
    <>
      <div style={s.card}>
        <div style={s.label}>Strumenti</div>
        <div style={s.title}>Assistente produzione</div>
        <div style={s.sub}>{safeName}</div>

        <div style={s.divider} />

        {/* Preferiti */}
        <div style={{ marginBottom: 12 }}>
          <div style={s.row}>
            <label style={s.toggle(isFavorite)} onClick={!busy ? doToggleFavorite : undefined}>
              <div style={s.toggleTrack(isFavorite)}>
                <div style={s.toggleThumb(isFavorite)} />
              </div>
              {isFavorite ? 'Nei preferiti' : 'Aggiungi ai preferiti'}
            </label>
            <span style={s.chip(isFavorite)}>{isFavorite ? '★ Preferito' : '☆ Non preferito'}</span>
          </div>
          <div style={{ fontSize: 11, color: TEXT_DIM, marginTop: 5, lineHeight: 1.5 }}>
            I preferiti appaiono in cima nelle ricerche e nei piani di post.
          </div>
        </div>

        <div style={s.divider} />

        {/* Alert disponibilità */}
        <div style={{ marginBottom: 12 }}>
          <div style={s.sectionTitle}><span>🔔</span> Avvisami quando si libera</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select style={s.select} value={lead ?? ''}
              onChange={(e) => setLead(e.target.value === '' ? null : Number(e.target.value))}>
              <option value="">Disattivo</option>
              <option value={7}>7 giorni prima</option>
              <option value={15}>15 giorni prima</option>
              <option value={30}>30 giorni prima</option>
            </select>
            <button style={s.btn('primary')} onClick={doSaveAlert}
              disabled={busy || (!canSaveAlert && lead !== null)}>
              {lead ? 'Salva' : 'Disattiva'}
            </button>
          </div>
        </div>

        <div style={s.divider} />

        {/* Priorità — coming soon */}
        <div style={{ opacity: 0.5, pointerEvents: 'none', userSelect: 'none' }}>
          <div style={{ ...s.row, marginBottom: 4 }}>
            <div style={s.sectionTitle}>⭐ Priorità nei piani di post</div>
            <span style={s.comingSoon}>Coming soon</span>
          </div>
          <div style={{ fontSize: 11, color: TEXT_DIM, marginBottom: 8 }}>Funzionalità in arrivo.</div>
          <button style={s.btn('default')} disabled>Aggiungi alle priorità</button>
        </div>
      </div>

      <Snackbar open={!!snack} autoHideDuration={2500} onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack?.sev || 'info'} onClose={() => setSnack(null)} variant="filled">
          {snack?.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
